import { APIError } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import { css } from "@emotion/css";
import { createAjv, isVisible } from "@jsonforms/core";
import * as Sentry from "@sentry/react";
import { Menu } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { useApplication } from "./ApplicationContext";
import { ApplicationSectionListItem } from "./ApplicationSectionListItem";
import {
  getDisabledSectionKeys,
  getGroupLeader,
  groupSectionsByGroupId,
} from "./utils";

// unique condition to handle cases where the summary page ("Review & Submit") is not the final section
// Currently only applies to applicant-portal for applicant-to-pay cases
interface Props {
  isSummaryCompleted: boolean;
  summaryText?: string;
}

export function ApplicationSectionList(props: Props) {
  const { t } = useTranslation();

  const { summaryText = t("Review & Submit"), isSummaryCompleted = false } =
    props;

  const {
    data,
    uischema,
    completedSections,
    currentSection,
    navigateToSection,
    partiallySaveData,
    invalidSections,
    additionalSections,
  } = useApplication();

  if (!uischema) {
    return null;
  }

  const ajv = createAjv();
  const disabledSectionsKeys = getDisabledSectionKeys(uischema, data, ajv);

  if (disabledSectionsKeys.length > 0) {
    disabledSectionsKeys.push("summary");
  }

  const groups = groupSectionsByGroupId(uischema.elements);
  const effectiveCurrentSection = getGroupLeader(currentSection, groups);

  return (
    <Menu
      aria-label="Application sections"
      className={styles.sections}
      selectionMode="single"
      disallowEmptySelection
      disabledKeys={disabledSectionsKeys}
      selectedKeys={[effectiveCurrentSection]}
      onSelectionChange={async (keys) => {
        const [section] = [...keys];

        try {
          await partiallySaveData();
        } catch (e) {
          if (e instanceof APIError && e.status === 400) {
            showToast(t("Error saving your progress"), {
              type: "error",
            });
            return;
          }

          showToast(t("Something went wrong, try again"), {
            type: "error",
          });
          Sentry.captureException(e);
        }

        navigateToSection(String(section));
      }}
    >
      {groups.map((group) => {
        if (group.sections.length > 1) {
          const firstSection = group.sections[0];
          const anyVisible = group.sections.some((s) =>
            isVisible(s, {}, "", ajv)
          );
          if (!anyVisible) return null;

          const isCompleted = group.sections.every(
            (s) =>
              disabledSectionsKeys.includes(s.name) ||
              completedSections?.includes(s.name)
          );
          const isInvalid = group.sections.some((s) =>
            invalidSections?.includes(s.name)
          );

          return (
            <ApplicationSectionListItem
              id={firstSection.name}
              sectionKey={firstSection.name}
              key={group.groupId ?? firstSection.name}
              icon="pending_actions"
              isCompleted={isCompleted}
              isInvalid={isInvalid}
              aria-label={`Navigate to section ${firstSection.name}`}
            >
              {firstSection.label}
            </ApplicationSectionListItem>
          );
        }

        const section = group.sections[0];
        const isCompleted =
          disabledSectionsKeys.includes(section.name) ||
          completedSections?.includes(section.name);
        const isInvalid = invalidSections?.includes(section.name);

        if (!isVisible(section, {}, "", ajv)) return null;

        return (
          <ApplicationSectionListItem
            id={section.name}
            sectionKey={section.name}
            key={section.name}
            icon="pending_actions"
            isCompleted={isCompleted}
            isInvalid={isInvalid}
            aria-label={`Navigate to section ${section.name}`}
          >
            {section.label}
          </ApplicationSectionListItem>
        );
      })}
      <ApplicationSectionListItem
        id="summary"
        sectionKey="summary"
        icon="overview"
        isCompleted={isSummaryCompleted}
      >
        {summaryText}
      </ApplicationSectionListItem>
      {additionalSections}
    </Menu>
  );
}

const styles = {
  sections: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    padding: "var(--space-2)",
    maxHeight: "100%",
    overflowY: "auto",
  }),
  sectionListItem: css(
    {
      padding: `${"var(--space-2)"} ${"var(--space-3)"}`,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      background: "transparent",
      border: "none",
      gap: "var(--space-2)",
    },
    getTextStyle("summary/nav-list")
  ),
};
